<template>
    <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" persistent>
        <q-card class="presupuesto-crud-dialog" style="min-width: 420px; max-width: 95vw;">
            <q-toolbar class="bg-gradient-primary text-white">
                <q-icon name="edit" size="28px" class="q-mr-sm" />
                <q-toolbar-title>
                    <div class="text-h6">Editar Datos del Presupuesto</div>
                    <div class="text-caption text-white-7">Presupuesto #{{ presup?.id }}</div>
                </q-toolbar-title>
                <q-btn dense flat icon="close" v-close-popup>
                    <q-tooltip>Cerrar (ESC)</q-tooltip>
                </q-btn>
            </q-toolbar>

            <q-card-section>
                <form @submit.prevent="validate">
                    <div class="row q-col-gutter-md q-mb-md">
                        <div class="col-12">
                            <GltnInputNumber
                                v-model.number="input.tipo_cambio"
                                label="Tipo de Cambio"
                                hint="Valor del dólar en pesos"
                                prefix="U$S"
                                :errors="res.getErrors('tipo_cambio')"
                                @update:modelValue="validate($event, 'tipo_cambio')"
                            >
                                <template v-slot:prepend>
                                    <q-icon name="attach_money" color="primary" />
                                </template>
                            </GltnInputNumber>
                        </div>
                    </div>
                    <div class="row q-col-gutter-md q-mb-md">
                        <div class="col-12">
                            <GltnSelectCondicionPago
                                label="Condición de Pago"
                                v-model="input.condicion_pago_id"
                                :errors="res.getErrors('condicion_pago_id')"
                                @update:modelValue="validate($event, 'condicion_pago_id')"
                            />
                        </div>
                    </div>
                    <div class="row q-col-gutter-md">
                        <div class="col-12">
                            <GltnSelectUbicacion
                                label="Ubicación"
                                v-model="input.ubicacion_id"
                                emit-value
                                map-options
                                :errors="res.getErrors('ubicacion_id')"
                                @update:modelValue="validate($event, 'ubicacion_id')"
                            />
                        </div>
                    </div>
                </form>
            </q-card-section>

            <q-separator />
            <q-card-actions class="q-pa-md bg-white">
                <div class="row full-width q-gutter-sm">
                    <div class="col">
                        <q-btn outline label="Cancelar" color="grey-7" icon="close" v-close-popup class="full-width" />
                    </div>
                    <div class="col">
                        <q-btn unelevated label="Guardar" :color="res.valid ? 'primary' : 'grey'" icon="save"
                            @click="save" :disable="!res.valid" :loading="loading" class="full-width">
                            <template v-slot:loading>
                                <q-spinner-hourglass size="20px" />
                            </template>
                        </q-btn>
                    </div>
                </div>
            </q-card-actions>

            <q-inner-loading :showing="loading">
                <q-spinner-hourglass size="50px" color="primary" />
            </q-inner-loading>
        </q-card>
    </q-dialog>
</template>

<script setup>
import GltnInputNumber from "@/components/form/GltnInputNumber.vue";
import GltnSelectCondicionPago from "@/components/form/GltnSelectCondicionPago.vue";
import GltnSelectUbicacion from "@/components/form/GltnSelectUbicacion.vue";
import PresupuestoService from "@/services/PresupuestoService";
import suite from "./PresupuestoCrudDialogValidation.js";
import { reactive, ref, watch } from "vue";
import { useQuasar } from "quasar";

const $q = useQuasar();
const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    presup: {
        type: Object,
        default: null,
    },
});
const emits = defineEmits(["update:modelValue", "updated"]);

const input = reactive({
    tipo_cambio: 0,
    condicion_pago_id: null,
    ubicacion_id: null,
});

const res = ref();
const loading = ref(false);
res.value = suite.get();
const validate = (value, name) => {
    res.value = suite(name, input);
};

const clearInput = () => {
    input.tipo_cambio = 0;
    input.condicion_pago_id = null;
    input.ubicacion_id = null;
    res.value = suite.get();
};

watch(
    () => props.modelValue,
    (isOpen) => {
        if (isOpen && props.presup) {
            input.tipo_cambio = props.presup.tipo_cambio ?? 0;
            input.condicion_pago_id =
                props.presup.condicion_pago_id ?? props.presup.condicion_pago?.E4_CODIGO ?? null;
            input.ubicacion_id = props.presup.ubicacion_id ?? null;
            validate();
        } else if (!isOpen) {
            clearInput();
        }
    },
    { immediate: true }
);

const save = async () => {
    loading.value = true;
    try {
        await PresupuestoService.datosUpdate(props.presup.id, { ...input });
        emits("updated");
        emits("update:modelValue", false);
    } catch (error) {
        console.log(error);
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.presupuesto-crud-dialog {
    border-radius: 16px;
    overflow: hidden;

    .bg-gradient-primary {
        background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    }
}

.text-white-7 {
    color: rgba(255, 255, 255, 0.7);
}
</style>
